import { useCallback } from 'react';
import deepClone from '../utils/deepClone';
import { emptyTarget, createTab } from '../engine/factories';
import { brokenHorde, splitHorde, splitLine } from '../engine/roster/hordeTools';

/**
 * The copy of a tab: a COPY starts fresh (full wounds and magnitude, no
 * criticals or conditions), a CLONE keeps its state and its log.
 */
function duplicateOf(src, clone) {
  const tgt = deepClone(src.target);
  // A copy or a clone of a horde in flight is a horde that stands (plan design-gaps, P7's review).
  if (tgt.horde?.broken) delete tgt.horde.broken;
  if (!clone) {
    tgt.wounds.current = tgt.wounds.max;
    tgt.criticalDamage = [];
    tgt.conditions = [];
    if (tgt.horde?.isHorde) tgt.horde.magnitude = tgt.horde.maxMagnitude;
  }
  const t = createTab(tgt, 'tab-' + Date.now());
  t.label = src.label + (clone ? ' (Clone)' : ' (Copy)');
  t.target.name = t.label;
  if (clone) t.narrativeLog = deepClone(src.narrativeLog);
  return t;
}

/** Drop a closed tab's pending sync write and its echo memory. */
function forgetSync(sync, tabId) {
  if (sync.tgtSyncTimerRef.current[tabId]) {
    clearTimeout(sync.tgtSyncTimerRef.current[tabId]);
    delete sync.tgtSyncTimerRef.current[tabId];
  }
  delete sync.lastTgtJsonRef.current[tabId];
}

/** The tabs with `fromId` moved to where `toId` is; the same array when either is missing. */
function moveTab(prev, fromId, toId) {
  const fi = prev.findIndex((t) => t.id === fromId);
  const ti = prev.findIndex((t) => t.id === toId);
  if (fi === -1 || ti === -1) return prev;
  const n = [...prev];
  const [moved] = n.splice(fi, 1);
  n.splice(ti, 0, moved);
  return n;
}

/**
 * Manages tab lifecycle: create, duplicate, close, rename, reorder, cycle.
 *
 * @param {object} params
 * @param {Array}    params.tabs               - Current tabs array
 * @param {Function} params.setTabs            - Tab state setter
 * @param {string}   params.activeTab          - Currently active tab ID
 * @param {Function} params.setActiveTab       - Active tab setter
 * @param {Function} params.setTabCtx          - Tab context-menu setter (cleared on duplicate/close)
 * @param {Function} params.setRenamingTab     - Renaming-tab setter (cleared on rename)
 * @param {Function} params.isLocked           - Permission lock checker
 * @param {Function} params.syncTargetToFirestore - Pushes a target to Firestore
 * @param {Function} params.updateTab          - Helper to update a tab and sync
 * @param {string|null} params.sessionId       - Current session ID (unused since the close stopped reading it)
 * @param {object}   params.sync               - Sync refs { tgtSyncTimerRef, lastTgtJsonRef }
 * @param {Function} [params.isDefencePending] - A tab answering an attack cannot be closed
 */
export default function useTabManagement(params) {
  const { tabs, setTabs, activeTab, setActiveTab, setTabCtx, setRenamingTab, isLocked } = params;
  const { syncTargetToFirestore, updateTab } = params;
  const cycleTab = (dir) => {
    const idx = tabs.findIndex((t) => t.id === activeTab);
    setActiveTab(tabs[(idx + dir + tabs.length) % tabs.length].id);
  };
  const handleNewTab = useNewTab({ isLocked, syncTargetToFirestore, setTabs, setActiveTab });
  const handleDuplicateTab = (tabId, clone) => {
    if (isLocked('manageTabs')) return null;
    const src = tabs.find((t) => t.id === tabId);
    if (!src) return null;
    const t = duplicateOf(src, clone);
    setTabs((p) => [...p, t]);
    setActiveTab(t.id);
    setTabCtx(null);
    syncTargetToFirestore(t.id, t.target);
    return t; // the tab, not the id - see useNewTab
  };
  /**
   * Split a horde in two (a GM tool, plan map-display B): the source keeps the
   * larger half, a new tab takes the rest. The new tab and the line to say are
   * handed back; null when there is nothing to split.
   */
  const handleSplitHorde = (tabId) => {
    const src = !isLocked('manageTabs') && tabs.find((t) => t.id === tabId);
    const halves = src && splitHorde(src.target);
    if (!halves) return null;
    updateTab(tabId, (t) => ({ ...t, target: halves.kept }));
    const t = createTab({ ...halves.split, name: `${src.label} (split)` }, 'tab-' + Date.now());
    setTabs((p) => [...p, t]);
    syncTargetToFirestore(t.id, t.target);
    return { ...t, line: splitLine(src.label, halves.kept.horde.magnitude, halves.split.horde.magnitude) };
  };
  /** Mark a horde broken (plan design-gaps, P7): every screen's plate flickers it out before the Break closes it. */
  const markHordeBroken = (tabId) => updateTab(tabId, (t) => ({ ...t, target: brokenHorde(t.target) }));
  // The one answer to "would a close go through?": the close obeys it, and the horde's Break asks it first.
  const canCloseTab = (tabId) => !isLocked('manageTabs') && !params.isDefencePending?.(tabId);
  const handleCloseTab = useCloseTab({ ...params, canCloseTab });
  const handleRenameTab = (tabId, name) => {
    updateTab(tabId, (t) => ({ ...t, label: name, target: { ...t.target, name } }));
    setRenamingTab(null);
  };
  const handleTabDrop = (fromId, toId) => {
    if (fromId !== toId) setTabs((prev) => moveTab(prev, fromId, toId));
  };
  return {
    cycleTab,
    handleNewTab,
    handleDuplicateTab,
    handleSplitHorde,
    markHordeBroken,
    canCloseTab,
    handleCloseTab,
    handleRenameTab,
    handleTabDrop,
  };
}

/**
 * Returns the TAB, not just its id (v8.76.0).
 *
 * `setTabs` is a scheduled state update, so a caller that wants to do
 * something with the new combatant in the same tick - admitting it to the
 * turn order and rolling its initiative, say - cannot look it up in the
 * roster yet. Handing back the thing that was just built removes the race
 * rather than timing it.
 */
function useNewTab({ isLocked, syncTargetToFirestore, setTabs, setActiveTab }) {
  return useCallback(() => {
    if (isLocked('manageTabs')) return null;
    const t = createTab(emptyTarget(), 'tab-' + Date.now());
    setTabs((p) => [...p, t]);
    setActiveTab(t.id);
    syncTargetToFirestore(t.id, t.target);
    return t;
  }, [isLocked, syncTargetToFirestore, setTabs, setActiveTab]);
}

/**
 * Close a tab - never while it is answering an attack.
 *
 * NO `prev.length <= 1` FLOOR. It predates v8.29.0, when an empty roster
 * became a real state that the app actually opens in - after which
 * refusing to reach it from here left the last combatant deletable only by
 * recalling them to a waiting room and dismissing them there. The long way
 * round was the only one that worked, and it left their turn-tracker chip
 * behind. The next active tab is `r[0]?.id`, undefined when that was the
 * last one - the empty-roster state, not an error.
 *
 * LOCAL STATE ONLY. This does NOT delete the combatants/* document - a
 * comment here claimed it did, via "the roster sync", and nothing ever
 * has. The caller in DeathWatchSimulator owns the departure: token,
 * document and turn participant. Without that the next snapshot re-adds
 * whoever was closed, which is why closing a tab appeared to do nothing at
 * all (v8.88.0).
 */
function useCloseTab({ canCloseTab, sync, setTabs, setActiveTab, setTabCtx }) {
  const { tgtSyncTimerRef, lastTgtJsonRef } = sync;
  return useCallback(
    (tabId) => {
      if (!canCloseTab(tabId)) return;
      setTabs((prev) => {
        forgetSync({ tgtSyncTimerRef, lastTgtJsonRef }, tabId);
        const r = prev.filter((t) => t.id !== tabId);
        setActiveTab((curActive) => (curActive === tabId ? r[0]?.id : curActive));
        setTabCtx(null);
        return r;
      });
    },
    [canCloseTab, tgtSyncTimerRef, lastTgtJsonRef, setTabs, setActiveTab, setTabCtx],
  );
}
